/***
 *
 *   ONGOING MONITORING
 *   Reviewer dashboard listing the account's monitoring enrolments (customer,
 *   rating, status, latest outcome, last-updated) with audited add / remove /
 *   re-add. Reads GET /api/ongoing-monitoring/cases; add posts /add; the row
 *   actions post /:id/remove and /:id/readd (each with a reason). Continuous
 *   monitoring is delivered by the external provider — a possible match flags
 *   the row here and notifies reviewers by email.
 *
 **********/

import React, { Fragment, useContext, useEffect, useState } from "react";
import {
  Animate,
  Button,
  Card,
  Message,
  Table,
  TitleRow,
  ViewContext,
  useAPI,
} from "components/lib";

const STATUS_LABELS = { monitored: "Monitored", removed: "Removed" };
const OUTCOME_LABELS = {
  pending: "Pending",
  clear: "Clear",
  possible_match: "Possible match",
};

function ratingLabel(rating) {
  if (!rating) return "-";
  return rating.charAt(0).toUpperCase() + rating.slice(1);
}

export function OngoingMonitoringView() {
  const context = useContext(ViewContext);
  const cases = useAPI("/api/ongoing-monitoring/cases");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const list = Array.isArray(cases?.data) ? cases.data : [];

    setRows(
      list.map((c) => {
        const verification = c.verification || {};
        const name = [verification.first_name, verification.last_name]
          .filter(Boolean)
          .join(" ");
        const customer = name || verification.email || "Unknown customer";

        return {
          id: c.id,
          customer,
          email: verification.email || "",
          rating: ratingLabel(c.rating),
          status: STATUS_LABELS[c.status] || c.status || "-",
          latest_outcome: OUTCOME_LABELS[c.latest_outcome] || c.latest_outcome || "-",
          last_screened_at: c.last_screened_at ? c.last_screened_at.slice(0, 10) : "",
        };
      })
    );
  }, [cases.data]);

  function addCustomer() {
    context.modal.show(
      {
        title: "Add a customer to monitoring",
        form: {
          verification_id: { label: "Verification ID", type: "text", required: true },
          reason: { label: "Reason", type: "text", required: true },
        },
        buttonText: "Add to monitoring",
        text: "Enrol a completed customer into ongoing PEP/Sanctions and adverse-media monitoring.",
        url: "/api/ongoing-monitoring/add",
        method: "POST",
      },
      () => window.location.reload()
    );
  }

  function remove(data) {
    context.modal.show(
      {
        title: "Remove from monitoring",
        form: { reason: { label: "Reason", type: "text", required: true } },
        text: "Stop monitoring this customer? This deregisters them with the provider. The audit trail is retained.",
        buttonText: "Remove",
        destructive: true,
        url: `/api/ongoing-monitoring/${data.id}/remove`,
        method: "POST",
      },
      () => window.location.reload()
    );
  }

  function readd(data) {
    context.modal.show(
      {
        title: "Resume monitoring",
        form: { reason: { label: "Reason", type: "text" } },
        text: "Re-enrol this customer so continuous monitoring resumes.",
        buttonText: "Resume monitoring",
        url: `/api/ongoing-monitoring/${data.id}/readd`,
        method: "POST",
      },
      () => window.location.reload()
    );
  }

  return (
    <Fragment>
      <Animate>
        <TitleRow title="Ongoing Monitoring">
          <Button small text="Add customer" action={addCustomer} />
        </TitleRow>

        <Message
          type="info"
          title="Customers under continuous monitoring"
          text="Completed customers are enrolled automatically when monitoring is enabled. The provider re-screens them continuously; a possible match is flagged here and reviewers are notified. Remove a customer to stop monitoring (audited)."
        />

        <Card>
          <Table
            search
            data={rows}
            loading={cases.loading}
            show={["customer", "email", "rating", "status", "latest_outcome", "last_screened_at"]}
            badge={{
              col: "latest_outcome",
              color: "green",
              condition: [
                { value: "Clear", color: "green" },
                { value: "Possible match", color: "red" },
                { value: "Pending", color: "blue" },
              ],
            }}
            actions={{
              custom: [
                { icon: "x-circle", action: remove, condition: { col: "status", value: "Monitored" } },
                { icon: "rotate-ccw", action: readd, condition: { col: "status", value: "Removed" } },
              ],
            }}
          />
        </Card>
      </Animate>
    </Fragment>
  );
}
